import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Calendar as CalendarIcon, Edit, FileImage } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Schedules() {
  const [schedules, setSchedules] = useState([]);
  const [contents, setContents] = useState([]);
  const [screens, setScreens] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  
  const [formData, setFormData] = useState({
    content_id: '',
    screen_ids: [],
    start_date: '',
    end_date: '',
    duration_seconds: 10,
    priority: 1,
    display_type: 'single'
  });

  useEffect(() => {
    fetchSchedules();
    fetchContents();
    fetchScreens();
  }, []);

  const fetchSchedules = async () => {
    try {
      const response = await axios.get(`${API_URL}/schedules`);
      setSchedules(response.data);
    } catch (error) {
      toast.error('Nie udało się pobrać harmonogramów');
    }
  };

  const fetchContents = async () => {
    try {
      const response = await axios.get(`${API_URL}/content`);
      setContents(response.data);
    } catch (error) {
      console.error('Failed to fetch contents');
    }
  };

  const fetchScreens = async () => {
    try {
      const response = await axios.get(`${API_URL}/screens`);
      setScreens(response.data);
    } catch (error) {
      console.error('Failed to fetch screens');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const scheduleData = {
        ...formData,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString()
      };
      
      if (editingSchedule) {
        await axios.put(`${API_URL}/schedules/${editingSchedule.id}`, scheduleData);
        toast.success('Harmonogram zaktualizowany');
      } else {
        await axios.post(`${API_URL}/schedules`, scheduleData);
        toast.success('Harmonogram dodany');
      }
      
      setOpen(false);
      resetForm();
      fetchSchedules();
    } catch (error) {
      toast.error('Błąd podczas zapisywania');
    }
  };

  const toggleScheduleActive = async (scheduleId, currentStatus) => {
    try {
      const schedule = schedules.find(s => s.id === scheduleId);
      if (!schedule) return;
      
      const updatedSchedule = {
        ...schedule,
        is_active: !currentStatus,
        // Remove fields that shouldn't be sent
        id: undefined,
        created_at: undefined
      };
      
      // Clean up undefined fields
      Object.keys(updatedSchedule).forEach(key => 
        updatedSchedule[key] === undefined && delete updatedSchedule[key]
      );
      
      await axios.put(`${API_URL}/schedules/${scheduleId}`, updatedSchedule);
      toast.success(`Harmonogram ${!currentStatus ? 'aktywowany' : 'dezaktywowany'}`);
      fetchSchedules();
    } catch (error) {
      console.error('Toggle error:', error);
      toast.error('Nie udało się zmienić statusu harmonogramu');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten harmonogram?')) return;
    
    try {
      await axios.delete(`${API_URL}/schedules/${id}`);
      toast.success('Harmonogram usunięty');
      fetchSchedules();
    } catch (error) {
      toast.error('Nie udało się usunąć harmonogramu');
    }
  };

  const resetForm = () => {
    setFormData({
      content_id: '',
      screen_ids: [],
      start_date: '',
      end_date: '',
      duration_seconds: 10,
      priority: 1,
      display_type: 'single'
    });
    setEditingSchedule(null);
  };

  const openEdit = (schedule) => {
    setEditingSchedule(schedule);
    const startDate = new Date(schedule.start_date);
    const endDate = new Date(schedule.end_date);
    
    setFormData({
      content_id: schedule.content_id,
      screen_ids: schedule.screen_ids,
      start_date: startDate.toISOString().slice(0, 16),
      end_date: endDate.toISOString().slice(0, 16),
      duration_seconds: schedule.duration_seconds,
      priority: schedule.priority,
      display_type: schedule.display_type
    });
    setOpen(true);
  };

  const getContentTitle = (contentId) => {
    const content = contents.find(c => c.id === contentId);
    return content ? content.title : 'Nieznana treść';
  };

  const getScreenNames = (screenIds) => {
    return screenIds.map(id => {
      const screen = screens.find(s => s.id === id);
      return screen ? screen.name : id;
    }).join(', ');
  };

  return (
    <Layout>
      <div data-testid="schedules-container" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Harmonogram</h1>
            <p className="text-zinc-400">Zaplanuj kiedy i gdzie wyświetlać treści</p>
          </div>
          <Dialog open={open} onOpenChange={(val) => {
            setOpen(val);
            if (!val) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-schedule-button" className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Dodaj harmonogram
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
              <DialogHeader>
                <DialogTitle>{editingSchedule ? 'Edytuj harmonogram' : 'Nowy harmonogram'}</DialogTitle>
                <DialogDescription className="text-zinc-400">
                  {editingSchedule ? 'Zaktualizuj harmonogram wyświetlania' : 'Zaplanuj wyświetlanie treści na ekranach'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="content_id">Treść</Label>
                  <Select 
                    value={formData.content_id} 
                    onValueChange={(val) => {
                      const selectedContent = contents.find(c => c.id === val);
                      const displayType = selectedContent?.type === 'ad_combined' ? 'combined' : 'single';
                      setFormData({...formData, content_id: val, display_type: displayType});
                    }}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue placeholder="Wybierz treść" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700 text-white">
                      {contents.map(content => (
                        <SelectItem key={content.id} value={content.id}>
                          {content.title} {content.type === 'ad_combined' && '(Łączona)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.content_id && (() => {
                    const selected = contents.find(c => c.id === formData.content_id);
                    return selected?.type === 'ad_combined' && (
                      <p className="text-xs text-blue-400 mt-1">
                        💡 Reklama łączona - wyświetli się na wszystkich wybranych ekranach jako jeden duży overlay
                      </p>
                    );
                  })()}
                </div>
                <div>
                  <Label>Ekrany</Label>
                  <div className="space-y-2 mt-2 max-h-40 overflow-y-auto bg-zinc-800 p-3 rounded">
                    {screens.map(screen => (
                      <label key={screen.id} className="flex items-center space-x-2 text-sm">
                        <input
                          type="checkbox"
                          checked={formData.screen_ids.includes(screen.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({...formData, screen_ids: [...formData.screen_ids, screen.id]});
                            } else {
                              setFormData({...formData, screen_ids: formData.screen_ids.filter(id => id !== screen.id)});
                            }
                          }}
                          className="rounded"
                        />
                        <span>{screen.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="display_type">Typ wyświetlania</Label>
                  <Select value={formData.display_type} onValueChange={(val) => setFormData({...formData, display_type: val})}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="single">Pojedynczy</SelectItem>
                      <SelectItem value="combined">Łączony (4 ekrany)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start_date">Data rozpoczęcia</Label>
                    <Input
                      id="start_date"
                      type="datetime-local"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      required
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div>
                    <Label htmlFor="end_date">Data zakończenia</Label>
                    <Input
                      id="end_date"
                      type="datetime-local"
                      value={formData.end_date}
                      onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                      required
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="duration_seconds">Czas trwania (s)</Label>
                    <Input
                      id="duration_seconds"
                      type="number"
                      value={formData.duration_seconds}
                      onChange={(e) => setFormData({...formData, duration_seconds: parseInt(e.target.value)})}
                      required
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div>
                    <Label htmlFor="priority">Priorytet</Label>
                    <Input
                      id="priority"
                      type="number"
                      value={formData.priority}
                      onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value)})}
                      required
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full btn-primary">
                  {editingSchedule ? 'Zaktualizuj harmonogram' : 'Dodaj harmonogram'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-4">
          {schedules.map((schedule) => {
            const content = contents.find(c => c.id === schedule.content_id);
            return (
              <Card key={schedule.id} data-testid={`schedule-card-${schedule.id}`} className="schedule-card bg-zinc-900/80 backdrop-blur-sm border-zinc-800">
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    {content && (
                      <div className="w-20 h-28 flex-shrink-0 rounded overflow-hidden bg-zinc-800">
                        {content.file_url ? (
                          content.type === 'video' ? (
                            <video 
                              src={process.env.REACT_APP_BACKEND_URL + content.file_url} 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <img 
                              src={process.env.REACT_APP_BACKEND_URL + content.file_url} 
                              alt={content.title}
                              className="w-full h-full object-cover"
                            />
                          )
                        ) : content.editor_data?.preview ? (
                          <img 
                            src={content.editor_data.preview} 
                            alt={content.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-zinc-600">
                            <FileImage className="w-8 h-8" />
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex-1">
                      <CardTitle className="text-white text-lg">{getContentTitle(schedule.content_id)}</CardTitle>
                      <CardDescription className="text-zinc-400 mt-1">
                        {getScreenNames(schedule.screen_ids)}
                      </CardDescription>
                    </div>
                    <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-2 px-3 py-1">
                      <Switch
                        checked={schedule.is_active}
                        onCheckedChange={() => toggleScheduleActive(schedule.id, schedule.is_active)}
                        className="data-[state=checked]:bg-green-500"
                      />
                      <span className={`text-xs ${
                        schedule.is_active ? 'text-green-400' : 'text-zinc-400'
                      }`}>
                        {schedule.is_active ? 'Aktywny' : 'Nieaktywny'}
                      </span>
                    </div>
                    <Button
                      onClick={() => openEdit(schedule)}
                      variant="outline"
                      size="sm"
                      className="border-blue-900 text-blue-400 hover:bg-blue-900/20"
                    >
                      <Edit className="w-3 h-3" />
                    </Button>
                    <Button
                      onClick={() => handleDelete(schedule.id)}
                      variant="outline"
                      size="sm"
                      className="border-red-900 text-red-400 hover:bg-red-900/20"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                    </div>
                  </div>
                </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-zinc-500">Rozpoczęcie</p>
                    <p className="text-white font-medium">
                      {new Date(schedule.start_date).toLocaleString('pl-PL')}
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-500">Zakończenie</p>
                    <p className="text-white font-medium">
                      {new Date(schedule.end_date).toLocaleString('pl-PL')}
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-500">Czas trwania</p>
                    <p className="text-white font-medium">{schedule.duration_seconds}s</p>
                  </div>
                  <div>
                    <p className="text-zinc-500">Priorytet</p>
                    <p className="text-white font-medium">{schedule.priority}</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-zinc-800">
                  <span className={`text-xs px-2 py-1 rounded ${
                    schedule.display_type === 'combined' 
                      ? 'bg-purple-500/20 text-purple-400' 
                      : 'bg-blue-500/20 text-blue-400'
                  }`}>
                    {schedule.display_type === 'combined' ? 'Wyświetlanie łączone' : 'Wyświetlanie pojedyncze'}
                  </span>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>

        {schedules.length === 0 && (
          <Card className="bg-zinc-900/80 backdrop-blur-sm border-zinc-800">
            <CardContent className="py-12 text-center">
              <CalendarIcon className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500 mb-4">Brak zaplanowanych harmonogramów</p>
              <p className="text-zinc-600 text-sm">Kliknij "Dodaj harmonogram" aby rozpocząć</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}